import { create } from 'zustand';

// ── Auth Store ───────────────────────────────────────────────
interface User {
  id: string;
  email: string;
  displayName: string;
  avatar?: string;
  orgId: string;
  roles: string[];
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  setUser: (user) => set({ user, isAuthenticated: !!user, isLoading: false }),
  setLoading: (isLoading) => set({ isLoading }),
  logout: () => {
    localStorage.removeItem('eurion_token');
    set({ user: null, isAuthenticated: false });
  },
}));

// ── Chat Store ───────────────────────────────────────────────
interface Room {
  id: string;
  name: string;
  type: 'direct' | 'group' | 'channel';
  isEncrypted: boolean;
  unreadCount: number;
  /** For DM rooms: the other person's display name shown in the sidebar */
  displayName?: string;
  /** User IDs of room members (used for DM lookups) */
  memberIds?: string[];
  /** Latest top-level message, for the room-list preview and recency order */
  lastMessage?: { content: string; senderId: string; at: string; type?: string } | null;
  /** Set for team channel rooms: members come from the team, not the chat member controls */
  teamId?: string | null;
}

interface Message {
  id: string;
  roomId: string;
  senderId: string;
  senderName: string;
  content: string;
  type: 'text' | 'file' | 'system' | 'call_invite';
  format?: 'text' | 'markdown' | 'html';
  threadId?: string;
  threadReplyCount?: number;
  /** 'sending' and 'failed' only exist on this client for optimistic messages */
  deliveryStatus?: 'sending' | 'failed' | 'sent' | 'delivered' | 'read';
  createdAt: string;
  isEdited?: boolean;
  editedAt?: string;
  isDeleted?: boolean;
  reactions?: { emoji: string; user_ids: string[]; count: number }[];
  mentions?: string[];
  metadata?: Record<string, unknown>;
  linkPreviews?: { url: string; urlHash: string; title: string | null; description: string | null; imageUrl: string | null; siteName: string | null; faviconUrl: string | null }[];
}

interface ChatState {
  rooms: Room[];
  activeRoomId: string | null;
  messages: Record<string, Message[]>;
  pinnedMessages: Record<string, Message[]>;
  /** Cursor for loading older messages per room */
  cursors: Record<string, string | null>;
  /** Whether more messages exist per room */
  hasMore: Record<string, boolean>;
  /** Active thread message ID (for ThreadPanel) */
  activeThreadId: string | null;
  setRooms: (rooms: Room[]) => void;
  /** null closes the open conversation (phones show the room list then) */
  setActiveRoom: (roomId: string | null) => void;
  updateRoom: (roomId: string, updates: Partial<Room>) => void;
  addRoom: (room: Room) => void;
  addMessage: (roomId: string, message: Message) => void;
  setMessages: (roomId: string, messages: Message[]) => void;
  prependMessages: (roomId: string, messages: Message[]) => void;
  updateMessage: (roomId: string, messageId: string, updates: Partial<Message>) => void;
  deleteMessage: (roomId: string, messageId: string) => void;
  removeRoom: (roomId: string) => void;
  setPinnedMessages: (roomId: string, messages: Message[]) => void;
  addPinnedMessage: (roomId: string, message: Message) => void;
  removePinnedMessage: (roomId: string, messageId: string) => void;
  setCursor: (roomId: string, cursor: string | null) => void;
  setHasMore: (roomId: string, hasMore: boolean) => void;
  setActiveThread: (messageId: string | null) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  rooms: [],
  activeRoomId: null,
  messages: {},
  pinnedMessages: {},
  cursors: {},
  hasMore: {},
  activeThreadId: null,
  setRooms: (rooms) => set({ rooms }),
  setActiveRoom: (activeRoomId) => set({ activeRoomId }),
  updateRoom: (roomId, updates) =>
    set((state) => ({
      rooms: state.rooms.map((r) => (r.id === roomId ? { ...r, ...updates } : r)),
    })),
  addRoom: (room) =>
    set((state) => ({
      rooms: state.rooms.some((r) => r.id === room.id) ? state.rooms : [room, ...state.rooms],
    })),
  addMessage: (roomId, message) =>
    set((state) => {
      const existing = state.messages[roomId] ?? [];
      // Deduplicate: skip if a message with the same id already exists
      if (existing.some((m) => m.id === message.id)) return state;
      const isPreviewable = !message.threadId && message.type !== 'call_invite' && message.type !== 'system';
      return {
        messages: {
          ...state.messages,
          [roomId]: [...existing, message],
        },
        rooms: isPreviewable
          ? state.rooms.map((r) => (r.id === roomId
            ? { ...r, lastMessage: { content: message.content, senderId: message.senderId, at: message.createdAt } }
            : r))
          : state.rooms,
      };
    }),
  setMessages: (roomId, messages) =>
    set((state) => ({
      messages: { ...state.messages, [roomId]: messages },
    })),
  updateMessage: (roomId, messageId, updates) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [roomId]: (state.messages[roomId] ?? []).map((m) =>
          m.id === messageId ? { ...m, ...updates } : m,
        ),
      },
    })),
  deleteMessage: (roomId, messageId) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [roomId]: (state.messages[roomId] ?? []).map((m) =>
          m.id === messageId ? { ...m, content: '[deleted]', isDeleted: true } : m,
        ),
      },
    })),
  removeRoom: (roomId) =>
    set((state) => {
      const newRooms = state.rooms.filter((r) => r.id !== roomId);
      return {
        rooms: newRooms,
        activeRoomId: state.activeRoomId === roomId
          ? (newRooms.length > 0 ? newRooms[0].id : null)
          : state.activeRoomId,
      };
    }),
  setPinnedMessages: (roomId, messages) =>
    set((state) => ({
      pinnedMessages: { ...state.pinnedMessages, [roomId]: messages },
    })),
  addPinnedMessage: (roomId, message) =>
    set((state) => ({
      pinnedMessages: {
        ...state.pinnedMessages,
        [roomId]: [message, ...(state.pinnedMessages[roomId] ?? [])],
      },
    })),
  removePinnedMessage: (roomId, messageId) =>
    set((state) => ({
      pinnedMessages: {
        ...state.pinnedMessages,
        [roomId]: (state.pinnedMessages[roomId] ?? []).filter((m) => m.id !== messageId),
      },
    })),
  prependMessages: (roomId, messages) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [roomId]: [...messages, ...(state.messages[roomId] ?? [])],
      },
    })),
  setCursor: (roomId, cursor) =>
    set((state) => ({
      cursors: { ...state.cursors, [roomId]: cursor },
    })),
  setHasMore: (roomId, hasMore) =>
    set((state) => ({
      hasMore: { ...state.hasMore, [roomId]: hasMore },
    })),
  setActiveThread: (messageId) => set({ activeThreadId: messageId }),
}));

// ── Call Store ────────────────────────────────────────────────
interface CallState {
  activeCallId: string | null;
  callRoomId: string | null;
  callType: 'audio' | 'video';
  isMuted: boolean;
  isVideoOn: boolean;
  isScreenSharing: boolean;
  participants: { userId: string; displayName: string; isMuted: boolean; isVideoOn: boolean }[];
  setActiveCall: (callId: string | null, callType?: 'audio' | 'video', roomId?: string | null) => void;
  toggleMute: () => void;
  toggleVideo: () => void;
  toggleScreenShare: () => void;
  setParticipants: (participants: CallState['participants']) => void;
}

export const useCallStore = create<CallState>((set) => ({
  activeCallId: null,
  callRoomId: null,
  callType: 'video',
  isMuted: false,
  isVideoOn: true,
  isScreenSharing: false,
  participants: [],
  setActiveCall: (activeCallId, callType, roomId) => set({
    activeCallId,
    callRoomId: roomId ?? null,
    callType: callType ?? 'video',
    isMuted: false,
    isVideoOn: (callType ?? 'video') === 'video',
    isScreenSharing: false,
  }),
  toggleMute: () => set((state) => ({ isMuted: !state.isMuted })),
  toggleVideo: () => set((state) => ({ isVideoOn: !state.isVideoOn })),
  toggleScreenShare: () => set((state) => ({ isScreenSharing: !state.isScreenSharing })),
  setParticipants: (participants) => set({ participants }),
}));

// ── Team Store ────────────────────────────────────────────────
export interface TeamChannel {
  id: string;
  name: string;
  description?: string;
  type: 'public' | 'private' | 'shared';
  is_archived: boolean;
  unreadCount: number;
}

export interface Team {
  id: string;
  name: string;
  description?: string;
  icon_url?: string;
  slug: string;
  member_count: number;
  channel_count: number;
  role: 'member' | 'admin' | 'owner';
  channels?: TeamChannel[];
}

export interface ChannelTab {
  id: string;
  channel_id: string;
  tab_type: 'files' | 'meetings' | 'tasks' | 'whiteboard' | 'integrations' | 'custom';
  label: string;
  position: number;
  config?: Record<string, unknown>;
}

interface TeamState {
  teams: Team[];
  activeTeamId: string | null;
  activeChannelId: string | null;
  channelTabs: Record<string, ChannelTab[]>;
  setTeams: (teams: Team[]) => void;
  setActiveTeam: (teamId: string | null) => void;
  setActiveChannel: (channelId: string | null) => void;
  setChannelTabs: (channelId: string, tabs: ChannelTab[]) => void;
  updateTeamChannels: (teamId: string, channels: TeamChannel[]) => void;
}

export const useTeamStore = create<TeamState>((set) => ({
  teams: [],
  activeTeamId: null,
  activeChannelId: null,
  channelTabs: {},
  setTeams: (teams) => set({ teams }),
  setActiveTeam: (activeTeamId) => set({ activeTeamId, activeChannelId: null }),
  setActiveChannel: (activeChannelId) => set({ activeChannelId }),
  setChannelTabs: (channelId, tabs) =>
    set((state) => ({ channelTabs: { ...state.channelTabs, [channelId]: tabs } })),
  updateTeamChannels: (teamId, channels) =>
    set((state) => ({
      teams: state.teams.map((t) =>
        t.id === teamId ? { ...t, channels } : t,
      ),
    })),
}));

// ── Incoming Call Store ───────────────────────────────────────
export interface IncomingCall {
  callId: string;
  callerId: string;
  callerName: string;
  roomId: string;
  callType: 'audio' | 'video';
}

interface IncomingCallState {
  incomingCall: IncomingCall | null;
  setIncomingCall: (call: IncomingCall | null) => void;
}

export const useIncomingCallStore = create<IncomingCallState>((set) => ({
  incomingCall: null,
  setIncomingCall: (incomingCall) => set({ incomingCall }),
}));

// ── Notification Store ───────────────────────────────────────
interface Notification {
  id: string;
  title: string;
  body: string;
  type: string;
  targetId?: string;
  /** Optional message ID for deep-link scrolling */
  messageId?: string;
  read: boolean;
  createdAt: string;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Notification) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  setNotifications: (notifications: Notification[]) => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  unreadCount: 0,
  addNotification: (notification) =>
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + (notification.read ? 0 : 1),
    })),
  markRead: (id) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n,
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    })),
  markAllRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    })),
  setNotifications: (notifications) =>
    set({
      notifications,
      unreadCount: notifications.filter((n) => !n.read).length,
    }),
}));
